import { useEffect, useRef, useState } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

import {
  applyTimeOfDay,
  dayOffsetLabel,
  describeMoment,
  formatDayLabel,
  formatTimeOfDay,
  parseTimeOfDay,
  shiftDays,
  TIME_CHOICES,
} from '../util/timeOfDay';

/**
 * The Review modal's check-in / check-out field (GRAVY-10814, Figma
 * 39621:199579) — a formatted clock time in a 120px box, with a clock control
 * that opens a picker.
 *
 * Replaces a raw `<input type="datetime-local">`, which rendered in the UA's
 * own font and metrics, drew its own spinner and calendar affordance, and
 * truncated its text at the designed width.
 *
 * ## The two things the first version got wrong
 *
 * **GRAVY-10817 — the clock did nothing.** It was drawn because the design
 * draws it, which made it a lie: it looked like the control that opens a
 * picker and was decoration. An icon that signals interaction has to either
 * work or not be there, and here it should work — typing is a fine expert path
 * but a poor only path.
 *
 * **GRAVY-10818 — the date was fixed.** Showing a time only was faithful to
 * the mock and wrong for the industry. An overnight shift checks out on the
 * NEXT calendar day, and with no way to say so, "10:00 PM to 6:00 AM" was
 * indistinguishable from a check-out typed in before its check-in — so the
 * GRAVY-10812 range check rejected a legitimate shift. It also made the
 * over-24-hours rule unreachable from the UI, which is why that scenario could
 * not be verified.
 *
 * So the picker owns the DAY (a stepper, because a shift's real correction is
 * "this landed tomorrow", not "jump to an arbitrary date") and offers times on
 * a 15-minute grid for convenience. The field's own text box still takes an
 * exact minute, so a 5:01 PM capture can be typed and is never rounded.
 *
 * The value in and out is the `datetime-local` string the modal already holds,
 * so `netHours`, `timesheetEditError` and the Approve payload are untouched —
 * and because the date travels in that value, Net Hours has always calculated
 * correctly across a day boundary. It just could not be entered until now.
 */

type Props = {
  /** `YYYY-MM-DDTHH:mm` — the full moment being edited. */
  value: string;
  onChange: (next: string) => void;
  ariaLabel: string;
  testId: string;
  /** Paints the border red; the reason is named beneath the card. */
  invalid?: boolean;
  /**
   * The other side's moment, used ONLY to show "+1 day" when this one lands on
   * a different date. Passed for check-out (anchored to check-in); omitted for
   * check-in, which has nothing earlier to be offset from.
   */
  anchor?: string | null;
};

const TimeOfDayField = ({
  value,
  onChange,
  ariaLabel,
  testId,
  invalid = false,
  anchor = null,
}: Props) => {
  const [draft, setDraft] = useState(() => formatTimeOfDay(value));
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Re-seed when the value changes from outside — a different worker's review
  // loading, or the server's answer after a decision. Skipped while focused so
  // it cannot yank characters out from under someone mid-type.
  useEffect(() => {
    if (!editing) setDraft(formatTimeOfDay(value));
  }, [value, editing]);

  // Close on an outside click or Escape. Registered only while open, so the
  // modal keeps exactly one listener per open picker rather than one per field
  // for the life of the modal.
  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Bring the current time into view when the list opens, rather than
  // anchoring at 12:00 AM and making the admin scroll to where they already are.
  useEffect(() => {
    if (!open) return;
    // Optional call, matching the sibling picker in CreateOrderV2's TimeField:
    // jsdom does not implement `scrollIntoView`, and a convenience scroll must
    // never be the reason a picker throws on open.
    listRef.current
      ?.querySelector('[data-selected="true"]')
      ?.scrollIntoView?.({ block: 'center' });
  }, [open]);

  /**
   * Push the value up as soon as the text parses, rather than waiting for blur,
   * so Net Hours recalculates while the admin is still looking at it (AC).
   * The DRAFT text is left exactly as typed; only blur normalises it, which
   * keeps the caret still.
   */
  const push = (text: string) => {
    const mins = parseTimeOfDay(text);
    if (mins == null) return;
    const next = applyTimeOfDay(value, mins);
    if (next && next !== value) onChange(next);
  };

  const commit = () => {
    const mins = parseTimeOfDay(draft);
    const next = mins == null ? null : applyTimeOfDay(value, mins);
    // Unreadable input reverts rather than clearing the timesheet: dropping a
    // check-out because of a typo is far worse than ignoring the typo.
    if (!next) {
      setDraft(formatTimeOfDay(value));
      return;
    }
    if (next !== value) onChange(next);
    setDraft(formatTimeOfDay(next));
  };

  const stepDay = (days: number) => {
    const next = shiftDays(value, days);
    if (next) onChange(next);
  };

  const chooseTime = (label: string) => {
    const mins = parseTimeOfDay(label);
    if (mins == null) return;
    const next = applyTimeOfDay(value, mins);
    if (next) {
      onChange(next);
      setDraft(formatTimeOfDay(next));
    }
    setOpen(false);
  };

  const offset = dayOffsetLabel(anchor, value);

  return (
    <div className="dashv2-review-timewrap" ref={wrapRef}>
      <div
        className={`dashv2-review-timefield${invalid ? ' is-invalid' : ''}`}
        // The box shows a time, so the DATE would otherwise be invisible.
        // Never hidden — on hover here, on the stepper when the picker is open,
        // and as a badge whenever it differs from the other side.
        title={describeMoment(value)}
        data-testid={`${testId}-field`}
      >
        <input
          type="text"
          className="dashv2-review-timefield-input"
          value={draft}
          aria-label={ariaLabel}
          data-testid={`${testId}-input`}
          onFocus={() => setEditing(true)}
          onChange={(e) => {
            setDraft(e.target.value);
            push(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
              setEditing(false);
              (e.target as HTMLInputElement).blur();
            } else if (e.key === 'Escape') {
              setDraft(formatTimeOfDay(value));
              setEditing(false);
            }
          }}
          onBlur={() => {
            commit();
            setEditing(false);
          }}
        />
        {/* GRAVY-10817. A real button, not the bare glyph it used to be —
            `heroicons-outline/clock` as the design names it, now carrying the
            behaviour its shape promises. */}
        <button
          type="button"
          className="dashv2-review-timefield-btn"
          aria-label={`Choose ${ariaLabel.toLowerCase()} date and time`}
          aria-expanded={open}
          data-testid={`${testId}-picker-toggle`}
          onClick={() => setOpen((o) => !o)}
        >
          <ClockIcon aria-hidden="true" />
        </button>
      </div>

      {/* GRAVY-10818. Says the shift crosses midnight, on the field itself, so
          "10:00 PM → 6:00 AM" reads as an overnight shift rather than an
          eight-hour mistake. */}
      {offset && (
        <span
          className="dashv2-review-dayoffset"
          data-testid={`${testId}-day-offset`}
        >
          {offset}
        </span>
      )}

      {open && (
        <div
          className="dashv2-review-timepicker"
          role="dialog"
          aria-label={`${ariaLabel} date and time`}
          data-testid={`${testId}-picker`}
        >
          {/* A STEPPER rather than a calendar: the correction a timesheet
              actually needs is "this landed on the next day", and a full
              date grid would invite moving a shift to an unrelated week. */}
          <div className="dashv2-review-daystep">
            <button
              type="button"
              aria-label="Previous day"
              data-testid={`${testId}-day-prev`}
              onClick={() => stepDay(-1)}
            >
              <ChevronLeftIcon aria-hidden="true" />
            </button>
            <span data-testid={`${testId}-day-label`}>
              {formatDayLabel(value)}
            </span>
            <button
              type="button"
              aria-label="Next day"
              data-testid={`${testId}-day-next`}
              onClick={() => stepDay(1)}
            >
              <ChevronRightIcon aria-hidden="true" />
            </button>
          </div>

          <div className="dashv2-review-timelist" ref={listRef}>
            {TIME_CHOICES.map((label) => {
              const selected = label === formatTimeOfDay(value);
              return (
                <button
                  type="button"
                  key={label}
                  className="dashv2-review-timechoice"
                  data-selected={selected ? 'true' : undefined}
                  aria-pressed={selected}
                  onClick={() => chooseTime(label)}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <p className="dashv2-review-pickernote">
            Type in the field for an exact minute.
          </p>
        </div>
      )}
    </div>
  );
};

export default TimeOfDayField;
